import { Fragment, ReactNode, createContext, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isWeaveUser as getIsWeaveUser, onWeaveTokenChange } from '@frontend/auth-helpers';
import { getUserChannel, setUserChannel, usePollForUpdates, type Channel } from '@frontend/automatic-updates';
import appConfig from '@frontend/env';
import type { IconName } from '@frontend/icons';
import { useSettingsPathLabelMap } from '@frontend/settings-routing';
import { sentry } from '@frontend/tracking';
import { siteChannels } from '../constants/constants';
import { usePrimaryNavContent } from '../layout/global-nav/nav-content';
import { buildMappedRoutesLookup } from '../utils/routing-legacy/routes';
import { useTabManagement } from './tab-management';

// Event name constant for Sherlock devtools refresh events
const SHERLOCK_REFRESH_EVENT = 'sherlock:app-refresh';
const SHERLOCK_SEND_ROUTES_EVENT = 'sherlock:send-routes';

type RefreshEventDetail = {
  type: 'hard' | 'soft';
  reason?: string;
};

type ContextValue = {
  currentVersion: string | undefined;
  updateAvailable: boolean;
  shouldPreventUpdate: boolean;
  setShouldPreventUpdate: (value: boolean) => void;
  refresh: (type: 'hard' | 'soft', reason?: string) => void;
  hasAlreadyRefreshed: (reason?: string) => boolean;
} & ReturnType<typeof useChannelAssignment>;

const AppContext = createContext<ContextValue>({} as ContextValue);

type FlatRoute = {
  id: string;
  name: string;
  path: string;
  description?: string[];
  breadcrumbs?: string[];
  iconName?: IconName;
};

export const GetRoutes = memo(() => {
  const primaryRoutes = usePrimaryNavContent();
  const pathLabelMap = useSettingsPathLabelMap();
  const mappedRoutesLookup = useMemo(() => buildMappedRoutesLookup(pathLabelMap), [pathLabelMap]);

  const sendRoutes = useCallback(() => {
    const routes = {
      setting: mappedRoutesLookup.reduce((acc, route) => {
        if (!route.path) return acc;
        const pathWithoutSettings = route.path.replace('/settings', '').replace(/\/$/, '');
        const lastBreadcrumbSegment = route.labelPath.split('/').pop() ?? ''; // paths that are using the URL param (e.g. '/:id') will not match any of the pathLabelMap keys. So fallback to the last breadcrumb segment since that will be in a readable format.
        const name =
          (pathLabelMap[pathWithoutSettings as keyof typeof pathLabelMap]?.label as string) ?? lastBreadcrumbSegment;
        const description =
          (pathLabelMap as Record<string, { description: string }>)?.[pathWithoutSettings]?.description ?? '';
        const flat: FlatRoute = {
          id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
          path: route.path,
          name,
          description: description ? [description] : [],
          breadcrumbs: route.labelPath.split('/').filter(Boolean),
        };
        return [...acc, flat];
      }, new Array<FlatRoute>()),
      feature: primaryRoutes.reduce((acc, { iconName, root, subNavItems }) => {
        if (!subNavItems) return acc;

        return [
          ...acc,
          ...subNavItems.reduce((accumulator, subRoute) => {
            const description = 'description' in subRoute && subRoute.description ? [subRoute.description] : [];

            if (description.length === 0) return accumulator;

            const path = 'relativePath' in subRoute ? subRoute.relativePath : '';
            const flat: FlatRoute = {
              id: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa',
              path: `${root}${path}`,
              name: subRoute.label,
              description,
              iconName,
            };

            return [...accumulator, flat];
          }, new Array<FlatRoute>()),
        ];
      }, new Array<FlatRoute>()),
    };

    console.log('Routes', routes);
  }, [mappedRoutesLookup, pathLabelMap, primaryRoutes]);

  useEffect(() => {
    window.addEventListener(SHERLOCK_SEND_ROUTES_EVENT, sendRoutes);
    return () => {
      window.removeEventListener(SHERLOCK_SEND_ROUTES_EVENT, sendRoutes);
    };
  }, [sendRoutes]);

  return null;
});

GetRoutes.displayName = 'GetRoutes';

export const AppProvider = ({ children }: { children: ReactNode }) => {
  useTabManagement();

  const { currentChannel, setCurrentChannel } = useChannelAssignment();
  const [shouldPreventUpdate, setShouldPreventUpdate] = useState(false);
  const url = appConfig.PUBLISH_URL ? 'https://' + appConfig.PUBLISH_URL + '/_m/version' : undefined;

  const { updateAvailable, currentVersion } = usePollForUpdates(60000, url);

  /* This effect is for setting the Sentry tag for the update channel, which allows us to filter Sentry issues by channel. It runs whenever the currentChannel changes. */
  useEffect(() => {
    if (currentChannel?.slug) {
      sentry.instance.setTag('channel', currentChannel.slug);
    }
  }, [currentChannel?.slug]);

  useEffect(() => {
    if (currentVersion) {
      localStorage.setItem('current-version', currentVersion);
    } else {
      localStorage.removeItem('current-version');
    }
  }, [currentVersion]);

  const [key, setKey] = useState(Math.random());

  // Allows resetting the app to force a remount of the entire provider tree and components.
  // Prevents refresh loops by tracking local storage keys with a one minute expiry.
  const refresh = useCallback((type: 'hard' | 'soft', reason?: string) => {
    if (!reason) {
      reason = 'unknown';
    }
    const now = Date.now();
    const keyName = `last-refresh-reason-${reason}`;

    if (!hasAlreadyRefreshed(reason)) {
      localStorage.setItem(keyName, JSON.stringify({ expiry: now }));
      if (type === 'hard') {
        sentry.error({
          error: `page refresh: ${reason}`,
          topic: 'infra',
          team: 'frontend-ops',
          severityLevel: 'error',
          addContext: {
            name: 'reason',
            context: {
              windowPathname: window.location.pathname,
              reason,
            },
          },
        });
        location.reload();
      } else {
        // just updates the key to trigger a remount of the app full provider tree
        setKey(Math.random());
      }
    } else {
      sentry.error({
        error: `page refresh loop attempted: ${reason}`,
        topic: 'infra',
        team: 'frontend-ops',
        severityLevel: 'error',
        addContext: {
          name: 'reason',
          context: {
            windowPathname: window.location.pathname,
            reason,
          },
        },
      });
    }
    removeExpiredRefreshReasons(reason);
  }, []);

  useEffect(() => {
    removeExpiredRefreshReasons();
  }, []);

  // Listen for refresh events from Sherlock devtools
  useEffect(() => {
    const handleSherlockRefresh = (event: Event) => {
      const customEvent = event as CustomEvent<RefreshEventDetail>;
      const { type, reason } = customEvent.detail;
      refresh(type, reason);
    };

    window.addEventListener(SHERLOCK_REFRESH_EVENT, handleSherlockRefresh);
    return () => {
      window.removeEventListener(SHERLOCK_REFRESH_EVENT, handleSherlockRefresh);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      currentVersion,
      updateAvailable,
      shouldPreventUpdate,
      setShouldPreventUpdate,
      refresh,
      hasAlreadyRefreshed: (reason?: string) => hasAlreadyRefreshed(reason),
      currentChannel,
      setCurrentChannel,
    }),
    [
      currentVersion,
      updateAvailable,
      refresh,
      shouldPreventUpdate,
      setShouldPreventUpdate,
      currentChannel,
      setCurrentChannel,
    ]
  );
  return (
    <AppContext.Provider value={value}>
      <Fragment key={key}>{children}</Fragment>
    </AppContext.Provider>
  );
};

export const useApp = () => {
  return useContext(AppContext);
};

const getExpiry = (item: string | null) => {
  if (!item) return undefined;
  try {
    return (JSON.parse(item) as { expiry: number }).expiry;
  } catch {
    return undefined;
  }
};

// Checks if a refresh has already occurred for a given reason within the last minute
const hasAlreadyRefreshed = (reason?: string) => {
  if (!reason) reason = 'unknown';
  const keyName = `last-refresh-reason-${reason}`;
  const now = Date.now();

  const expiry = getExpiry(localStorage.getItem(keyName));
  return !!expiry && now - expiry < 60 * 1000;
};

const removeExpiredRefreshReasons = (exclude?: string) => {
  const now = Date.now();
  // Remove any refresh reasons with an expiry over one minute
  Object.keys(localStorage)
    .filter((k) => k.startsWith('last-refresh-reason-') && (exclude ? !k.endsWith(exclude) : true))
    .forEach((k) => {
      const expiry = getExpiry(localStorage.getItem(k));
      if (!expiry || now - expiry > 60 * 1000) {
        localStorage.removeItem(k);
      }
    });
};

const useChannelAssignment = () => {
  const [isWeaveUser, setIsWeaveUser] = useState(() => getIsWeaveUser());
  const [currentChannel, setCurrentChannel] = useState<Channel | undefined>(() =>
    getUserChannel(isWeaveUser, siteChannels)
  );

  useEffect(() => {
    const handleUserChange = () => {
      setIsWeaveUser(getIsWeaveUser());
    };
    const unsubscribe = onWeaveTokenChange(handleUserChange);
    return () => {
      unsubscribe();
    };
  }, [setIsWeaveUser, getIsWeaveUser]);

  useEffect(() => {
    setCurrentChannel(siteChannels ? getUserChannel(isWeaveUser, siteChannels) : undefined);
  }, [isWeaveUser, siteChannels]);

  const setChannel = useCallback(
    (channel: Channel) => {
      setUserChannel(channel);
      setCurrentChannel(channel);
    },
    [setUserChannel, setCurrentChannel]
  );

  return {
    currentChannel,
    setCurrentChannel: setChannel,
  };
};
